import React from "react";
import { useTranslate } from "@refinedev/core";
import { Col, Flex, Row } from "antd";
import type { AddonActivation, AddonDetail, QuizPreparation } from "types/";
import { AddonItemOpenRateCard } from "./AddonItemOpenRateCard";
import { AddonPanelHeader } from "./AddonPanelHeader";
import { mergeItems, quizEffect } from "./utils/metrics";
import { QuizEffectCard } from "./QuizEffectCard";
import { useCoverageAndTimeStats } from "./hooks/useCoverageAndTimeStats";

const T_PREFIX = "media.projects.analytics.addons";

export type StandoutsPanelProps = {
  activation: AddonActivation;
  detail: AddonDetail;
  /** null without a quiz, or when nobody finished it: no quiz effect card. */
  preparation: QuizPreparation | null;
  /** The preparation request failed: the quiz effect card says so. */
  preparationFailed?: boolean;
  projectSessions: number;
};

export const StandoutsPanel: React.FC<StandoutsPanelProps> = ({
  activation,
  detail,
  preparation,
  preparationFailed = false,
  projectSessions,
}) => {
  const t = useTranslate();
  const stats = useCoverageAndTimeStats(activation);
  const effect = preparation ? quizEffect(preparation, "standouts") : null;
  // One entry per position: an item edited since is listed once per wording.
  const positions = mergeItems(detail.items);

  return (
    <Flex vertical gap={16}>
      <AddonPanelHeader
        addon="standouts"
        activation={activation}
        projectSessions={projectSessions}
        // The positions also hold items removed since they were seen.
        count={t(`${T_PREFIX}.itemCount`, {
          count: activation.total_items ?? positions.length,
        })}
        stats={stats}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <AddonItemOpenRateCard
            addon="standouts"
            activation={activation}
            positions={positions}
          />
        </Col>

        {effect || preparationFailed ? (
          <Col xs={24} lg={12}>
            <QuizEffectCard effect={effect} />
          </Col>
        ) : null}
      </Row>
    </Flex>
  );
};
